import React, { useMemo, useEffect, useState, useCallback } from 'react';
import CTable from '../CTable';
import { MsgContentTypeEnum } from './constants';
import CMarkdown from '../CMarkdown';
import Bar from './Bar';
import MetricCard from './MetricCard';
import MetricTrend from './MetricTrend';
import { CnSwitch } from '@sto/cn-ui';
import Text from './Text';
import styles from './index.module.scss';

// const isMobile = window.navigator.userAgent.match(/(iPhone|iPod|Android|ios)/i);

interface CBiProps {
  content: any;
  question: any;
  chartIndex?: number;
  activeMetricField?: any;
  drillDownDimension?: any;
  currentDateOption?: any;
  selectDateOption?: any;
  referenceColumn?: any;
  data?: any;
}

const CBi: React.FC<CBiProps> = ({ 
  content, 
  question,
  chartIndex = 0,
  activeMetricField,
  drillDownDimension,
  currentDateOption,
  selectDateOption,
  referenceColumn,
  data
}) => {
  const { queryColumns, queryMode } = content || {};
  const queryResults = content?.queryResults || [];

  const [triggerResize, setTriggerResize] = useState(false);
  const [msgContentType, setMsgContentType] = useState<MsgContentTypeEnum | undefined>(undefined);
  const [forceShowTable, setForceShowTable] = useState(false);
  const [isSimpleMode] = useState(false);

  const columns = queryColumns?.filter(item => item.showType !== 'more') || [];
  const singleData = queryResults.length === 1;
  const dateField = columns.find(item => item.showType === 'DATE' || item.type === 'DATE') || null;
  const categoryField = columns.filter(item => item.showType === 'CATEGORY') || [];
  const metricFields = columns.filter(item => item.showType === 'NUMBER') || [];

  const getMsgContentType = useCallback((): MsgContentTypeEnum | undefined => {
    if (!columns) {
      return undefined;
    }
    const isDslMetricCard =
      queryMode === 'LLM_S2SQL' && singleData && metricFields.length === 1 && columns.length === 1;
    const isMetricCard = (queryMode?.includes('METRIC') || isDslMetricCard) && singleData;
    const isText = !queryColumns?.length;

    if (isText) {
      return MsgContentTypeEnum.TEXT;
    }

    if (isMetricCard) {
      return MsgContentTypeEnum.METRIC_CARD;
    }

    const isTable =
      !isText &&
      !isMetricCard &&
      (categoryField.length > 1 ||
        queryMode === 'TAG_DETAIL' ||
        queryMode === 'ENTITY_DIMENSION' ||
        queryResults?.length === 1 ||
        (categoryField.length === 1 && metricFields.length === 0));

    if (isTable) {
      return MsgContentTypeEnum.TABLE;
    }
    const isMetricTrend =
      dateField &&
      metricFields.length > 0 &&
      categoryField.length <= 1 &&
      !(metricFields.length > 1 && categoryField.length > 0) &&
      !queryResults.every(item => item[dateField.nameEn] === queryResults[0][dateField.nameEn]);
    if (isMetricTrend) {
      return MsgContentTypeEnum.METRIC_TREND;
    }

    const isMetricBar =
      categoryField?.length > 0 &&
      metricFields?.length === 1 &&
      queryResults?.length <= 5;

    if (isMetricBar) {
      return MsgContentTypeEnum.METRIC_BAR;
    }
    return MsgContentTypeEnum.TABLE;
  }, [columns, queryMode, singleData, metricFields.length, queryColumns?.length, categoryField.length, queryResults, dateField]);

  const onResize = useCallback(() => {
    setTriggerResize(true);
    setTimeout(() => {
      setTriggerResize(false);
    }, 0);
  }, []);

  useEffect(() => {
    const contentType = getMsgContentType();
    setMsgContentType(contentType);
  }, [getMsgContentType]);

  useEffect(() => {
    // 使用 ResizeObserver 监听 scroll-container 的尺寸变化
    let resizeObserver: ResizeObserver | null = null;
    const scrollContainer = document.getElementById('scroll-container');
    if (scrollContainer && window.ResizeObserver) {
      try {
        resizeObserver = new ResizeObserver((entries) => {
          // 使用 requestAnimationFrame 来避免在 ResizeObserver 回调中同步修改 DOM
          requestAnimationFrame(() => {
            try {
              for (const entry of entries) {
                if (entry.target === scrollContainer) {
                  onResize();
                }
              }
            } catch (error) {
              console.warn('ResizeObserver callback error:', error);
            }
          });
        });
        resizeObserver.observe(scrollContainer);
      } catch (error) {
        console.warn('ResizeObserver creation error:', error);
      }
    }

    window.addEventListener('resize', onResize);
    return () => {
      window.removeEventListener('resize', onResize);
      if (resizeObserver) {
        resizeObserver.disconnect();
      }
    };
  }, []);

  useEffect(() => {
    onResize();
  }, [onResize]);


  const tableColumns = queryColumns?.map((item) => {
    return {
      ...item,
      name: item.name,
      code: item.bizName,
    }
  }) || [];


  const getMsgContent = useMemo(() => {
    if (typeof content === 'string') {
      return <div style={{ wordWrap: 'break-word' }}>{content}</div>;
    }
    if (!queryMode) {
      return <div>数据结构不匹配，组件无法正常渲染</div>;
    }
    let contentType = msgContentType;
    if (isSimpleMode) {
      contentType = MsgContentTypeEnum.MARKDOWN;
    }
    if (forceShowTable) {
      contentType = MsgContentTypeEnum.TABLE;
    }
    switch (contentType) {
      case MsgContentTypeEnum.TEXT:
        return <Text columns={queryColumns} referenceColumn={referenceColumn} dataSource={queryResults} />;
      case MsgContentTypeEnum.METRIC_CARD:
        return (
          <MetricCard
            data={{ ...content, queryColumns, queryResults }}
            question={question}
            loading={false}
          />
        );
      case MsgContentTypeEnum.TABLE:
        return (
          <CTable
            content={{
              ...content,
              header: tableColumns,
              rows: queryResults || [],
            }}
            {...({ scroll: { x: 'max-content' } } as any)}
            paging={true}
          />
        );
      case MsgContentTypeEnum.METRIC_TREND:
        return (
          <MetricTrend
            data={{
              ...content,
              queryColumns,
              queryResults,
            }}
            question={question}
            loading={false}
            chartIndex={chartIndex}
            triggerResize={triggerResize}
            activeMetricField={activeMetricField}
            drillDownDimension={drillDownDimension}
            currentDateOption={currentDateOption}
            onSelectDateOption={selectDateOption}
          />
        );
      case MsgContentTypeEnum.METRIC_BAR:
        return (
          <Bar
            data={{ ...content, queryColumns, queryResults }}
            question={question}
            triggerResize={triggerResize}
            loading={false}
            metricField={metricFields[0]}
          />
        );
      case MsgContentTypeEnum.MARKDOWN:
        return (
          <CMarkdown content={data?.textResult || ''} />
        );
      default:
        return (
          <CTable
            content={{
              header: tableColumns,
              rows: queryResults || [],
            }}
            {...({ scroll: { x: 'max-content' } } as any)}
            paging={true}
          />
        );
    }
  }, [msgContentType, isSimpleMode, forceShowTable, queryColumns, queryResults, content, question, tableColumns, triggerResize, metricFields]);

	return (
    <>
      <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: 10 }} className={styles.switchContainer}>
        {/* @ts-ignore */}
        {([MsgContentTypeEnum.METRIC_TREND, MsgContentTypeEnum.METRIC_BAR] as any[]).includes(msgContentType) && (
          <CnSwitch
            checkedChildren="表格"
            unCheckedChildren="图表"
            value={forceShowTable}
            onChange={checked => {
              setForceShowTable(checked);
            }}
          />
        )}
      </div>
      {getMsgContent}
    </>
  );
};

export default CBi;
